/**
 * BreakpointHint — the contextual strip shown while authoring a NON-desktop
 * view. It states the override direction at the moment of risk: edits here fork
 * this device off the Desktop base rather than changing the base itself. Names
 * "Desktop" explicitly so the cascade (Desktop → Tablet → Mobile) is legible.
 * Nothing on desktop (that IS the base). react-strict-dom (html.* + css.create).
 */
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { spacing, radii } from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import type { Breakpoint } from "./breakpoints.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";

const s = css.create({
  strip: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    height: sizes.chip,
    paddingLeft: spacing.ms,
    paddingRight: spacing.ms,
    borderRadius: radii.sm,
    backgroundColor: colors.infoBg,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.infoBorder,
    color: colors.accent,
    fontSize: "11.5px",
    fontWeight: typography.fontWeightMedium,
    boxShadow: shadows.sm,
  },
  strong: { fontWeight: typography.fontWeightBold },
  muted: { color: colors.accent },
});

const icon = (path: React.ReactNode) => (
  <svg
    viewBox="0 0 24 24"
    width={13}
    height={13}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block" }}
    aria-hidden
  >
    {path}
  </svg>
);
const TABLET = icon(<rect x="5" y="3" width="14" height="18" rx="2" />);
const PHONE = icon(<rect x="7" y="3" width="10" height="18" rx="2" />);

/** The under-toolbar strip naming what an edit at this tier does. */
export function BreakpointHint({ breakpoint }: { breakpoint: Breakpoint }) {
  if (breakpoint === "desktop") return null;
  const label = breakpoint === "tablet" ? "Tablet" : "Mobile";
  const noun = breakpoint === "tablet" ? "tablets" : "phones";
  return (
    <html.div style={s.strip} role="status">
      {breakpoint === "tablet" ? TABLET : PHONE}
      <html.span>
        <html.span style={s.strong}>{label} view</html.span> — edits override{" "}
        <html.span style={s.strong}>Desktop</html.span>
        <html.span style={s.muted}> on {noun} only</html.span>
      </html.span>
    </html.div>
  );
}
